import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import splitFilesIntoChunks from "utilities/splitIntoChunks";
import { uploadFilesFromGoogleDrive } from "api/fileManager";
import { message } from "antd";

const FileUploadContext = createContext<any | null>(null);

const STATS_UPDATE_INTERVAL_MS = 250;
const SPEED_WINDOW_MS = 5000;
const KEEP_TAB_OPEN_NOTICE = "Keep this tab open until the upload finishes.";

type UploadPhase = 'idle' | 'uploading' | 'processing' | 'completed' | 'failed' | 'aborted';

type UploadFileStatus = {
  name: string;
  size: number;
  uploadedBytes: number;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'failed' | 'aborted';
  error?: string;
};

type UploadStats = {
  phase: UploadPhase;
  uploadedBytes: number;
  totalBytes: number;
  bytesPerSecond: number;
  secondsRemaining: number | null;
  elapsedSeconds: number;
  currentFileNames: string[];
  files: UploadFileStatus[];
  notice?: string;
};

const initialUploadStats: UploadStats = {
  phase: 'idle',
  uploadedBytes: 0,
  totalBytes: 0,
  bytesPerSecond: 0,
  secondsRemaining: null,
  elapsedSeconds: 0,
  currentFileNames: [],
  files: [],
};

const getUploadErrorMessage = (error: any) => {
  if (error?.response?.status) return `Server responded ${error.response.status}`;
  if (error?.code === 'ECONNABORTED') return 'Request timed out';
  return error?.message || 'Upload failed';
};

export const FileUploadProvider = ({ children }: any) => {
  const [showUploader, setShowUploader] = useState(false);
  const [fileDetails, setFileDetails] = useState<any>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [totalUpload, setTotalUpload] = useState(0);
  const [filesUploaded, setFilesUploaded] = useState(0);
  const [totalFilesAmount, setTotalFilesAmount] = useState(0);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [filesWithErrors, setFilesWithErrors] = useState<any>([]);
  const [uploaderMessage, setUploaderMessage] = useState({ type: "none", message: "" });
  const [fileType, setFileType] = useState<string | null>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [isUploading, setIsUploading] = useState(false); // New state for tracking upload status
  const [hasError, setHasError] = useState(false);
  const [uploadStats, setUploadStats] = useState<UploadStats>(initialUploadStats);
  const lastStatsUpdate = useRef(0);

  useEffect(() => {
    if (!isUploading) return;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [isUploading]);

  const onFileAbort = () => {
    if (abortController) {
      abortController.abort();
      setUploaderMessage({ type: "error", message: "Upload aborted" });
    }
    setFileDetails(null);
    setFileLoading(false);
    setShowProgressBar(false);
    setIsUploading(false);
  };

  const handleProgressReset = () => {
    setTotalUpload(0);
    setFilesUploaded(0);
    setTotalFilesAmount(0);
    setUploadStats(initialUploadStats);
    setIsUploading(false); // Reset upload status
  };

  const handleFile = (filesUploaded: any) => {
    if (!filesUploaded && !fileDetails?.fileName) {
      setFileDetails(null);
    } else if (filesUploaded) {
      setTotalFilesAmount(filesUploaded.length);
      setFileDetails(splitFilesIntoChunks(filesUploaded, 2)); // Assuming stepsCount is 2
    }
  };

  const handleFileSubmit = async (type: any, fileUploaderApiConfig: any) => {
    setFilesUploaded(0);
    setFileLoading(true);
    setShowProgressBar(true);
    setIsUploading(true);

    const controller = new AbortController();
    setAbortController(controller);

    const bundles: any[][] = (fileDetails || []).map((bundle: any) => Object.values(bundle));
    const files: UploadFileStatus[] = bundles.flat().map((file: any) => ({
      name: file.name,
      size: file.size,
      uploadedBytes: 0,
      status: 'pending',
    }));
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    const startedAt = Date.now();
    const speedSamples: { time: number; bytes: number }[] = [];
    let completedBytes = 0;
    let fileIndexOffset = 0;

    const reportProgress = (phase: UploadPhase, uploadedBytes: number, currentFileNames: string[], force = false, notice = KEEP_TAB_OPEN_NOTICE) => {
      const now = Date.now();
      if (!force && now - lastStatsUpdate.current < STATS_UPDATE_INTERVAL_MS) return;
      lastStatsUpdate.current = now;

      speedSamples.push({ time: now, bytes: uploadedBytes });
      while (speedSamples.length > 2 && now - speedSamples[0].time > SPEED_WINDOW_MS) speedSamples.shift();
      const oldest = speedSamples[0];
      const windowSeconds = (now - oldest.time) / 1000;
      const bytesPerSecond = windowSeconds > 0 ? (uploadedBytes - oldest.bytes) / windowSeconds : 0;

      setUploadStats({
        phase,
        uploadedBytes,
        totalBytes,
        bytesPerSecond,
        secondsRemaining: bytesPerSecond > 0 ? (totalBytes - uploadedBytes) / bytesPerSecond : null,
        elapsedSeconds: (now - startedAt) / 1000,
        currentFileNames,
        files: files.map((file) => ({ ...file })),
        notice,
      });
      setTotalUpload(totalBytes > 0 ? (uploadedBytes / totalBytes) * 100 : 0);
    };

    reportProgress('uploading', 0, [], true);

    try {
      let filesUploadedSoFar = 0;
      for (const processedFiles of bundles) {
        if (controller.signal.aborted) break;

        const bundleFiles = files.slice(fileIndexOffset, fileIndexOffset + processedFiles.length);
        fileIndexOffset += processedFiles.length;
        const fileNames = bundleFiles.map((file) => file.name);
        const bundleBytes = bundleFiles.reduce((sum, file) => sum + file.size, 0);
        const formData = new FormData();
        processedFiles.forEach((file: any) => formData.append("file", file));
        bundleFiles.forEach((file) => { file.status = 'uploading'; });

        const requestOptions = {
          signal: controller.signal,
          onUploadProgress: (event: any) => {
            const bundleLoaded = Math.min(event.loaded, bundleBytes);
            let remaining = bundleLoaded;
            bundleFiles.forEach((file) => {
              file.uploadedBytes = Math.min(remaining, file.size);
              remaining -= file.uploadedBytes;
            });
            const isSent = bundleLoaded >= bundleBytes;
            if (isSent) bundleFiles.forEach((file) => { file.status = 'processing'; });
            reportProgress(isSent ? 'processing' : 'uploading', completedBytes + bundleLoaded, fileNames, isSent);
          },
        };

        try {
          const response = type === "volume"
            ? await fileUploaderApiConfig.uploadVolumesForPointClouds(formData, requestOptions)
            : await fileUploaderApiConfig.uploadImagesForPointClouds(formData, requestOptions);

          if (response.status === 200) {
            filesUploadedSoFar += processedFiles.length;
            setFilesUploaded(filesUploadedSoFar);
            bundleFiles.forEach((file) => { file.status = 'completed'; file.uploadedBytes = file.size; });
          } else {
            throw Object.assign(new Error("Upload failed"), { response });
          }
        } catch (error: any) {
          if (error.name === "AbortError" || controller.signal.aborted) break;
          const reason = getUploadErrorMessage(error);
          bundleFiles.forEach((file) => { file.status = 'failed'; file.error = reason; });
          setFilesWithErrors((prev: any) => [...prev, ...fileNames]);
        }

        completedBytes += bundleBytes;
        reportProgress('uploading', completedBytes, fileNames, true);
      }

      if (controller.signal.aborted) {
        files.forEach((file) => { if (file.status !== 'completed') file.status = 'aborted'; });
        reportProgress('aborted', completedBytes, [], true, 'Upload aborted');
      } else {
        const failedCount = files.filter((file) => file.status === 'failed').length;
        const completedCount = files.length - failedCount;
        reportProgress(
          failedCount > 0 ? 'failed' : 'completed',
          completedBytes,
          [],
          true,
          failedCount > 0 ? `Uploaded ${completedCount} of ${files.length} files, ${failedCount} failed` : `All ${files.length} files uploaded`
        );
      }
    } catch (error) {
      setShowProgressBar(false);
      setIsUploading(false); // Reset upload status on error
    } finally {
      setFileLoading(false);
      setFileType(null);
      setAbortController(null);
      setIsUploading(false); // Reset upload status when done
    }
  };

  const handleLinkSubmit = async (link: string, type: 'volume' | 'img' | 'video' | 'e57' | 'las') => {
    setShowUploader(true);
    setFileType(type);
    setFileLoading(true);
    setIsUploading(true);
    setUploadStats({
      ...initialUploadStats,
      phase: 'processing',
      notice: 'The server is fetching the files from the link. This can take a while.',
    });

    const paths = {
      'volume': 'vol_calc_files',
      'img': 'raw_drone_image',
      'video': 'raw_drone_image',
      'e57': 'raw_drone_image',
      'las': 'raw_drone_image',
    };

    try {
      const response = await uploadFilesFromGoogleDrive(encodeURIComponent(link), paths[type]);

      if (response.status === 200) {
        setHasError(false);
        setUploaderMessage({ type: 'success', message: 'The files were uploaded' });
      } else {
        setHasError(true);
        setUploaderMessage({ type: 'error', message: 'The files from this link were not uploaded' });
      }
    } catch (error: any) {
      console.error("Upload failed with error:", error);

      setHasError(true);
      setUploaderMessage({
        type: 'error',
        message: 'The files from this link were not uploaded (server error)',
      });
    } finally {
      setFileLoading(false);
      setIsUploading(false);
      setUploadStats(initialUploadStats);
      setFileType(null);
      setShowUploader(false);
    }
  };

  return (
    <FileUploadContext.Provider value={{
      fileDetails,
      fileLoading,
      totalUpload,
      filesUploaded,
      totalFilesAmount,
      showProgressBar,
      filesWithErrors,
      onFileAbort,
      handleProgressReset,
      handleFile,
      fileType,
      setFileType,
      handleFileSubmit,
      setFileDetails,
      showUploader,
      setShowUploader,
      uploaderMessage,
      setUploaderMessage,
      setFileLoading,
      setTotalUpload,
      setFilesUploaded,
      setTotalFilesAmount,
      setShowProgressBar,
      setFilesWithErrors,
      isUploading,
      setIsUploading,
      handleLinkSubmit,
      hasError,
      setHasError,
      uploadStats
    }}>
      {children}
    </FileUploadContext.Provider>
  );
};

export const useFileUpload = () => useContext(FileUploadContext);